import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import DaletChar from '../components/DaletChar';
import { usePractice } from '../context/PracticeContext';
import axios from '../utils/axiosInstance';
import { IN_PROGRESS_TILE_STATUSES } from '../utils/taskStatusCounts';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { CALL_REASON_CONFIG } from '../utils/callReasonColors';
import { buildEscapeRows } from '../utils/escapeReasonColors';
import DateRangeDrawer from '../components/DateRangeDrawer';
import { formatDeRange, formatDeRangeFull, isoDay, PERIOD_OPTIONS, resolvePeriodRange } from '../utils/dateRange';
import { fetchPracticeDashboard, mergeDashboardData } from '../utils/dashboardData';
import { usePracticeScope } from '../hooks/usePracticeScope';
import PracticeScopeToggle from '../components/PracticeScopeToggle';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
// Stable identity for the not-yet-loaded case: it is a dependency of memos
// below, so a fresh [] per render would re-run them every render.
const NO_RECORDS = [];


// The seven task counters, in display order ('verpasster_anruf' last, without
// `alert`: a missed call is not open work). `status` is both the taskCounts key
// and the value handed to the Tasks page on a drill-through.
const TASK_TILES = [
  { status: 'pending', label: 'Ausstehend', alert: true },
  { status: 'unreachable', label: 'Nicht erreicht', alert: true },
  { status: 'in-progress', label: 'In Bearbeitung' },
  { status: 'weitergeleitet', label: 'Weitergeleitet' },
  { status: 'completed', label: 'Abgeschlossen' },
  { status: 'completed_by_deskbot', label: 'Abgeschlossen', dalet: true },
  { status: 'verpasster_anruf', label: 'Verpasster Anruf' },
];

// Hover texts for the Performance legend and the forward segments of the call band.
const PERF_TOOLTIPS = {
  direkt: 'Deskbot hat das Anliegen ohne Weiterleitung abgeschlossen. Stand bei Anrufende.',
  angenommen: 'Deskbot hat weitergeleitet, und die Verbindung stand nach der Annahme mindestens 10 Sekunden. Das kann auch eine Telefonanlage sein.',
  nichtBestaetigt: 'Deskbot hat weitergeleitet, eine Annahme ließ sich nicht feststellen: niemand hat abgenommen, besetzt, Anrufbeantworter, oder der Anbieter hat den Anruf übernommen.',
  offen: 'Aufgabe für das Praxisteam. Stand bei Anrufende.',
};

// How the Automatisierung score is built, in plain words (no points; the rules live in
// backend/src/api/automation/rules.py). One line per entry: the tooltip keeps the breaks.
const AUTOMATION_TOOLTIP = [
  'So entsteht der Wert',
  'Je Anruf: Wie viel von dem, was das Anliegen braucht, hat Deskbot selbst erledigt?',
  '',
  'Zählt:',
  '• Anrufer erkannt oder als Patient angelegt',
  '• Angaben erfasst, z. B. Verordnung oder Terminwunsch',
  '• Termine gebucht, abgesagt oder verschoben, Fragen beantwortet',
  '• Bestätigung per SMS oder E-Mail verschickt',
  '• SmartAB und Digital-AB: was erfasst wurde (als erledigt abgeschlossen: voll)',
  '• Bei Weiterleitung: die Arbeit davor',
  '',
  'Senkt den Wert:',
  '• ein Rückruf, den die Praxis noch machen muss',
  '• fehlgeschlagene Aktionen',
  '',
  'Zählt nicht gegen Deskbot: Fragen, die die Praxis ausgeschaltet hat.',
  'Ergebnis: Durchschnitt aller Anrufe im Zeitraum, ohne Auflegen während der Begrüßung.',
].join('\n');

const DashboardPage = () => {
  const navigate = useNavigate();
  // Session 10d: practiceId comes from PracticeContext — replaces the
  // keycloak.sub → userId → practiceId resolution chain that previously lived here.
  const { practiceId, loading: practiceLoading, isUserSuperAdmin } = usePractice();

  // UI state
  const [errorMessage, setErrorMessage] = useState('');
  const [timePeriod, setTimePeriod] = useState('week');
  // Heatmap toggle: 'count' = #Anrufe pro Slot, 'duration' = Summe Min/Slot
  const [heatmapMode, setHeatmapMode] = useState('count');
  // Trend chart toggle: 'reasons' = stacked by Anrufgrund, 'performance' = stacked by Outcome
  const [trendMode, setTrendMode] = useState('reasons');
  const [timePeriodOpen, setTimePeriodOpen] = useState(false);
  const timePeriodRef = useRef(null);
  // Custom Zeitraum ('range'): committed dates + whether the drawer is unfolded.
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [rangeOpen, setRangeOpen] = useState(false);

  const TIME_PERIOD_OPTIONS = PERIOD_OPTIONS;

  // Centralized date range calculation - single source of truth.
  // Delegates to the shared resolver so the Tasks page, which offers the same
  // Zeitraum control and receives this page's selection on a drill-through,
  // resolves every token to exactly the same inclusive calendar days.
  const calculateDateRange = (period) => {
    const { from, to } = resolvePeriodRange(period, customRange) || resolvePeriodRange('7');
    return { startDate: from, endDate: to };
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (timePeriodRef.current && !timePeriodRef.current.contains(e.target)) {
        setTimePeriodOpen(false);
        // Collapse the drawer too, so reopening the popover doesn't resume an
        // abandoned draft — the drawer re-seeds on every open.
        setRangeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Surface a friendly error when PracticeContext is settled but found no practice
  // (replaces the 404 branch of the deleted getPracticeId effect). Also clears the
  // isLoading spinner so the error screen is reachable in the no-practice case.
  useEffect(() => {
    if (!practiceLoading && !practiceId) {
      setErrorMessage('Keine Praxis gefunden. Bitte konfigurieren Sie Ihre Praxis in den Einstellungen.');
    } else if (practiceId) {
      setErrorMessage('');
    }
  }, [practiceLoading, practiceId]);

  // ─── "Alle Praxen" scope ──────────────────────────────────────────────────
  // Which practices this user may see summed into one view, and whether the
  // shared toggle is on. Both come from usePracticeScope — the same hook the
  // Tasks page uses, so the two pages cannot answer the question differently and
  // the choice carries from one to the other. `forTasks` stays false here: this
  // page must NOT list a task_manager's practices, because it is 403'd on their
  // call statistics.
  const scope = usePracticeScope();
  const { scopeIds, scopeKey, allPractices } = scope;

  // Fetch dashboard data — ONLY from call_statistics. Cached via React Query so
  // returning to the dashboard within staleTime paints instantly from cache and
  // refreshes in the background. The query fetches all three sources (summary,
  // full records, task counts) and hydrates the existing state vars via
  // onSuccess, leaving every downstream useMemo/getter untouched.
  // Key includes timePeriod because the date range derives from it.
  const { data: dashboardData, isFetching: dashboardFetching } = useQuery(
    // The range must be part of the key, not just the 'range' token — otherwise
    // every custom range shares one cache entry and shows the previous one's data.
    // Presets keep the 3-element shape: PracticeContext prefetches the EXACT key
    // ['dashboard', practiceId, 'week'], and react-query hashes the whole array,
    // so appending a 4th element unconditionally would miss that warm entry (two
    // parallel triple-fetches on every practice switch, plus a spinner).
    timePeriod === 'range'
      ? ['dashboard', practiceId, timePeriod, `${customRange.from}_${customRange.to}`]
      : ['dashboard', practiceId, timePeriod],
    () => {
      // Use centralized date calculation based on selected time period
      const { startDate, endDate } = calculateDateRange(timePeriod);
      return fetchPracticeDashboard(practiceId, startDate, endDate);
    },
    {
      // Disabled while aggregating so the two modes never fetch at once. The
      // cache entry survives, so toggling back paints from it.
      enabled: !!practiceId && !allPractices,
      onSuccess: () => {
        setErrorMessage('');
      },
      onError: (error) => {
        console.error('Error fetching dashboard data:', error);
        setErrorMessage('Fehler beim Laden der Dashboard-Daten');
      },
    }
  );

  // "Alle Praxen": the same three fetches, once per practice in scope, merged
  // into one payload of the same shape. One request per practice rather than a
  // bulk endpoint — each keeps its own membership check and its own RLS arming,
  // so no query anywhere reads across tenants (same reason the eject panel fans
  // out instead of posting a list of practices).
  const { data: aggregateData, isFetching: aggregateFetching } = useQuery(
    timePeriod === 'range'
      ? ['dashboard-all', scopeKey, timePeriod, `${customRange.from}_${customRange.to}`]
      : ['dashboard-all', scopeKey, timePeriod],
    async () => {
      const { startDate, endDate } = calculateDateRange(timePeriod);
      // allSettled, not all: one practice failing must not blank the other four.
      const settled = await Promise.allSettled(
        scopeIds.map((id) => fetchPracticeDashboard(id, startDate, endDate))
      );
      const loaded = settled.filter((r) => r.status === 'fulfilled').map((r) => r.value);
      if (loaded.length === 0) {
        throw new Error('Keine Praxis konnte geladen werden');
      }
      const merged = mergeDashboardData(loaded);
      return {
        ...merged,
        // Carried so the header can say the numbers are short of a practice
        // instead of quietly under-reporting. The lower of the two counts wins:
        // a practice can answer for call statistics and still fail on tasks, and
        // the note must never claim more practices than the weakest number on
        // screen actually covers.
        loadedCount: Math.min(loaded.length, merged.taskParts || loaded.length),
        scopeCount: scopeIds.length,
      };
    },
    {
      enabled: allPractices,
      onSuccess: () => {
        setErrorMessage('');
      },
      onError: (error) => {
        console.error('Error fetching aggregated dashboard data:', error);
        setErrorMessage('Fehler beim Laden der Dashboard-Daten');
      },
    }
  );

  // Automatisierung (super admins only; the backend refuses everyone else): the stored
  // automation score of the scored calls in the window. Pooled across practices by
  // pct_sum / calls, never by averaging the practice rates. Kept out of the dashboard
  // payload above, so a practice login never sends this request.
  const automationIds = allPractices ? scopeIds : (practiceId ? [practiceId] : []);
  const { data: automationData } = useQuery(
    ['dashboard-automation', allPractices ? scopeKey : practiceId, timePeriod,
      timePeriod === 'range' ? `${customRange.from}_${customRange.to}` : ''],
    async () => {
      const { startDate, endDate } = calculateDateRange(timePeriod);
      const settled = await Promise.allSettled(automationIds.map((id) => axios.get(
        `${API_BASE_URL}/call-statistics/practice/${id}/automation`,
        { params: { start_date: startDate, end_date: endDate } }
      )));
      const parts = settled.filter((r) => r.status === 'fulfilled').map((r) => r.value.data || {});
      const calls = parts.reduce((sum, p) => sum + (p.calls || 0), 0);
      const pctSum = parts.reduce((sum, p) => sum + (p.pct_sum || 0), 0);
      return { calls, pct: calls > 0 ? Math.round(pctSum / calls) : null };
    },
    { enabled: !!isUserSuperAdmin && automationIds.length > 0 }
  );
  const automationPct = automationData?.pct ?? null;

  // The active mode's payload IS the render source — no mirroring into state.
  // react-query v3's onSuccess does not fire on a cache hit, so reading data
  // directly is both what makes a cached revisit paint instantly AND what keeps
  // a mode switch from showing the other mode's numbers for a frame.
  const activeData = allPractices ? aggregateData : dashboardData;
  const activeFetching = allPractices ? aggregateFetching : dashboardFetching;
  const callStats = activeData?.callStats || null;
  const allCallRecords = activeData?.allCallRecords || NO_RECORDS;
  const taskCounts = activeData?.taskCounts || null;
  // Spinner only while there is nothing to show; a cached revisit or a cached
  // mode switch refreshes in the background without flashing it.
  const isLoading = activeFetching && !activeData;


  // Performance KPI, computed server-side (see the KPI_* constants in
  // routers/call_statistics.py). The unit is the TASK: a bot-finished call that left a
  // follow-up weighs 2 — 1 "Direkt erledigt" + 1 "Offen".
  //
  // `automated` is the whole non-open chunk. Two SUBSETS of it are split out, hence
  // subtracted, never added: `forwarded` (a transfer nobody demonstrably answered,
  // 'weitergeleitet') and `forwarded_answered` (a picked-up transfer, FWD_ANSWERED in the
  // backend; the call band uses the same predicate). What remains is "Direkt erledigt".
  // The centre percentage counts "Direkt erledigt" + "Weitergeleitet, angenommen"
  // (perfDone below), i.e. the same number as before the picked-up split.
  // Both default to 0 for a pre-deploy backend: the donut then degrades to its older
  // shape instead of mis-stating the KPI.
  const getPieChartData = () => {
    if (!callStats || !callStats.performance) return [];

    const { automated, open: offenCount, total: denominator, forwarded = 0, forwarded_answered: forwardedAnswered = 0 } = callStats.performance;
    if (!denominator) return [];

    const data = [
      { name: '𐤃 Direkt erledigt', value: automated - forwarded - forwardedAnswered, color: getComputedStyle(document.documentElement).getPropertyValue('--color-main').trim(), filter: 'completed_by_deskbot', tooltip: PERF_TOOLTIPS.direkt }
    ];

    if (forwardedAnswered > 0) {
      data.push({ name: 'Weitergeleitet, angenommen', value: forwardedAnswered, color: getComputedStyle(document.documentElement).getPropertyValue('--color-main-soft').trim(), filter: 'forwarded_answered', tooltip: PERF_TOOLTIPS.angenommen });
    }

    if (forwarded > 0) {
      data.push({ name: 'Weitergeleitet, nicht bestätigt', value: forwarded, color: getComputedStyle(document.documentElement).getPropertyValue('--color-secondary').trim(), filter: 'weitergeleitet', tooltip: PERF_TOOLTIPS.nichtBestaetigt });
    }

    if (offenCount > 0) {
      data.push({ name: 'Offen', value: offenCount, color: getComputedStyle(document.documentElement).getPropertyValue('--color-tertiary').trim(), filter: 'pending', tooltip: PERF_TOOLTIPS.offen });
    }

    return data;
  };

  const pieChartData = getPieChartData();

  // Calculate pie chart data for call reasons
  const getCallReasonsChartData = () => {
    if (!callStats || !callStats.call_reasons_breakdown) return [];

    const reasonsBreakdown = callStats.call_reasons_breakdown;

    // Labels and colors come from the shared CALL_REASON_CONFIG so the
    // pie slices stay in sync with the Grund badges on the Tasks page.
    const data = [];
    for (const [reason, count] of Object.entries(reasonsBreakdown)) {
      if (count > 0) {
        const cfg = CALL_REASON_CONFIG[reason];
        data.push({
          name: cfg?.label || reason,
          value: count,
          color: cfg?.bg || '#9ca3af',
          filterKey: reason
        });
      }
    }

    return data;
  };

  // ─── Drill-through to the Tasks page ──────────────────────────────────────
  // Every number on this Dashboard is computed over the selected Zeitraum, so
  // every drill-through hands that window over: the list a user lands on must
  // cover the same period as the count they clicked, or the two disagree.
  //
  // TasksPage's filters are isolated per user + practice and this page cannot
  // write those scoped keys (it does not know the scope), so the selection
  // travels as one transient handoff that TasksPage claims once on arrival.
  //
  // Single writer on purpose: this used to be three copies of setItem+navigate
  // and only one of them carried the window, so Grund and the KPI tiles silently
  // dropped it.
  //
  // Every drill-through is OFF while aggregating: the Tasks page is one
  // practice's list, and a count pooled over five practices has no single list
  // to land on. The tiles and rows render as plain elements in that mode — not
  // as disabled buttons — so there is no pointer cursor, no hover tint and no
  // tab stop offering a navigation that cannot happen.
  const goToTasks = (selection) => {
    localStorage.setItem('tasksPageHandoff', JSON.stringify({
      dayFilter: timePeriod,
      // Only read when dayFilter === 'range'; harmless otherwise.
      dayRange: customRange,
      // A drill-through states the COMPLETE filter state, never a delta. The
      // Tasks page restores the user's saved filters first, and those persist
      // across drill-throughs — so without these defaults one SmartAB click
      // would leave a sticky Übergabegrund that quietly guts the NEXT click on a
      // KPI tile or an Anrufgrund, showing far fewer rows than the number just
      // clicked. Every caller overrides the dimension it owns.
      callReasonFilters: [],
      escapeFilters: [],
      assigneeFilters: [],
      insuranceFilters: [],
      ...selection,
    }));
    navigate('/tasks');
  };

  // KPI tile. A tile can stand for more than one status (In Bearbeitung also
  // counts the super_admin-only 'processing_by_deskbot'), so hand over every
  // status it counted, else the list shows fewer rows than the number clicked.
  const handleStatusClick = (statusValue) => {
    goToTasks({
      statusFilters: statusValue === 'in-progress' ? IN_PROGRESS_TILE_STATUSES : [statusValue],
    });
  };

  // Anrufgrund row. Status resets to the default open view, preserving the old
  // removeItem('tasksPageFilter') "clean view" behaviour.
  const handleCallReasonClick = (reasonKey) => {
    if (!reasonKey) return;
    goToTasks({ callReasonFilters: [reasonKey], resetStatus: true });
  };

  // SmartAB handover row. Status goes to "Alle" ([]), NOT to the Offen preset the
  // Grund drill-down uses: a Grund is orthogonal to the call outcome, but an
  // escape code CORRELATES with it. Every code in the practice's
  // smartab_autocomplete_codes (the most frequent ones, e.g.
  // no_slot_within_prescription_deadline) marks its task completed_by_deskbot,
  // which is not in the Offen preset, so Offen would show ZERO rows for exactly
  // the reasons people click most. The count comes from call_statistics, which
  // ignores task status entirely, so the list must not narrow by status either.
  const handleEscapeReasonClick = (rowKey) => {
    if (!rowKey) return;
    goToTasks({ escapeFilters: [rowKey], statusFilters: [] });
  };

  const callReasonsData = getCallReasonsChartData();

  // Reasons shown before the "Weitere Gründe" toggle. Six keeps the card the
  // same order of height as the pies beside it.
  const ESCAPE_TOP_N = 6;
  const [escapeShowAll, setEscapeShowAll] = useState(false);

  // SmartAB handover breakdown. The API returns (code, reason, count) triples;
  // buildEscapeRows merges them by code and keeps the practice's own keywords
  // apart. escape_scope_total is the in-scope call count (instant hangups
  // excluded), i.e. the denominator for the handover rate.
  const escapeData = useMemo(() => {
    const built = buildEscapeRows(callStats?.escape_breakdown);
    const scopeTotal = Number(callStats?.escape_scope_total) || 0;
    return {
      ...built,
      scopeTotal,
      // Guard the divide: a range with zero in-scope calls has no rate to show.
      rate: scopeTotal > 0 ? (built.total / scopeTotal) * 100 : 0,
    };
  }, [callStats]);

  // Calculate pie chart data for call types
  const getCallTypesChartData = () => {
    if (!callStats || !callStats.call_type_breakdown) return [];

    const typeBreakdown = { ...callStats.call_type_breakdown };

    // German labels mapping. The two forward keys split with the donut's predicates
    // (see call_type_expr in routers/call_statistics.py), so both show the same counts.
    const typeLabels = {
      'ki': 'KI',
      'forwarded_answered': 'Weitergeleitet, angenommen',
      'forwarded': 'Weitergeleitet, nicht bestätigt',
      'voicemail': 'Digital-AB',
      'early_hangup': 'Sofort aufgelegt'
    };

    // Color mapping using CSS variables; the forward colours match the donut. Coral is
    // left to "Offen", so "Sofort aufgelegt" takes the neutral grey.
    const typeColors = {
      'ki': getComputedStyle(document.documentElement).getPropertyValue('--color-main').trim(),
      'forwarded_answered': getComputedStyle(document.documentElement).getPropertyValue('--color-main-soft').trim(),
      'forwarded': getComputedStyle(document.documentElement).getPropertyValue('--color-secondary').trim(),
      'voicemail': getComputedStyle(document.documentElement).getPropertyValue('--color-quaternary').trim(),
      'early_hangup': '#9ca3af'
    };

    const typeTooltips = {
      'forwarded_answered': PERF_TOOLTIPS.angenommen,
      'forwarded': PERF_TOOLTIPS.nichtBestaetigt
    };

    // Fixed segment order (the backend dict order is arbitrary), then any unknown key.
    const typeOrder = ['ki', 'forwarded_answered', 'forwarded', 'voicemail', 'early_hangup'];
    const orderedTypes = [...typeOrder, ...Object.keys(typeBreakdown).filter(type => !typeOrder.includes(type))];

    const data = [];
    for (const type of orderedTypes) {
      const count = typeBreakdown[type];
      if (count > 0) {
        data.push({
          name: typeLabels[type] || type,
          value: count,
          color: typeColors[type] || '#9ca3af',
          tooltip: typeTooltips[type]
        });
      }
    }

    return data;
  };

  const callTypesData = getCallTypesChartData();

  // Build the 7×N heatmap grid from raw call records. Each record's `call_time`
  // is parsed to its local weekday (Mon=0…Sun=6) and hour bucket.
  // Two modes share the same shape:
  //  - 'count'    → number of calls per slot
  //  - 'duration' → total minutes per slot (call_duration_seconds / 60)
  // Hours are limited to 7–20 (working window) so the grid stays scannable.
  const HEATMAP_HOUR_START = 7;
  const HEATMAP_HOUR_END = 20; // inclusive
  const HEATMAP_WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  // Localhost-only mock generator. Produces a realistic-looking distribution
  // (weekday peaks at 09–11 and 14–16, lighter Sat, near-zero Sun) so the
  // heatmap is visible without real data. Returns nothing in prod or when
  // real records exist. The mock spans the same date range as the selected
  // time period so both charts react to the dropdown the same way real data
  // would.
  const mockHeatmapRecords = useMemo(() => {
    const isLocal = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
    if (!isLocal) return [];
    if (allCallRecords && allCallRecords.length > 0) return [];
    // Deterministic PRNG so the mock doesn't reshuffle on every render.
    let seed = 1337;
    const rand = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    const today = new Date();
    const records = [];
    // Range = same window the backend would request for this timePeriod.
    const { startDate: startStr } = calculateDateRange(timePeriod);
    const start = new Date(startStr);
    const dayMs = 24 * 60 * 60 * 1000;
    const daysInRange = Math.max(1, Math.floor((today.setHours(0,0,0,0) - start.setHours(0,0,0,0)) / dayMs) + 1);
    // Reset `today` (mutated above by setHours).
    const nowRef = new Date();
    // 7 days back from today, weekday intensity profile.
    const dayIntensity = [1.0, 1.0, 1.0, 1.0, 0.95, 0.35, 0.05]; // Mo..So
    const hourIntensity = (h) => {
      // Twin peaks ~10:00 and ~15:00, taper at edges
      const morning = Math.exp(-Math.pow(h - 10, 2) / 4);
      const afternoon = Math.exp(-Math.pow(h - 15, 2) / 4);
      return Math.max(morning, afternoon);
    };
    // Realistic distribution from the pies in PROD: booking ~37%, cancelling
    // ~23%, info ~16%, callback ~13%, other ~9%, moving ~3%
    const pickReason = () => {
      const r = rand();
      if (r < 0.37) return 'booking';
      if (r < 0.60) return 'cancelling';
      if (r < 0.76) return 'info';
      if (r < 0.89) return 'callback';
      if (r < 0.98) return 'other';
      return 'moving';
    };
    // Status: completed_by_deskbot ~80%, forwarded ~8% (picked up ~5%, weitergeleitet ~3%), open ~12%
    const pickStatus = () => {
      const r = rand();
      if (r < 0.80) return { task_status: 'completed_by_deskbot', was_forwarded: false };
      if (r < 0.85) return { task_status: 'completed_by_deskbot', was_forwarded: true };
      if (r < 0.88) return { task_status: 'weitergeleitet', was_forwarded: true };
      return { task_status: 'pending', was_forwarded: false };
    };
    // Span the same date range as the selected time period.
    for (let d = 0; d < daysInRange; d++) {
      const date = new Date(nowRef);
      date.setDate(nowRef.getDate() - d);
      const jsDay = date.getDay();
      const dayIdx = jsDay === 0 ? 6 : jsDay - 1;
      const dayWeight = dayIntensity[dayIdx];
      for (let h = HEATMAP_HOUR_START; h <= HEATMAP_HOUR_END; h++) {
        const expected = dayWeight * hourIntensity(h) * 6;
        const count = Math.max(0, Math.round(expected + (rand() - 0.5) * 2));
        for (let i = 0; i < count; i++) {
          const callTime = new Date(date);
          callTime.setHours(h, Math.floor(rand() * 60), 0, 0);
          const status = pickStatus();
          records.push({
            call_time: callTime.toISOString(),
            call_duration_seconds: Math.round(60 + rand() * 240),
            call_reasons: [pickReason()],
            task_status: status.task_status,
            was_forwarded: status.was_forwarded,
          });
        }
      }
    }
    return records;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCallRecords, timePeriod, customRange]);

  const heatmapData = useMemo(() => {
    const hours = [];
    for (let h = HEATMAP_HOUR_START; h <= HEATMAP_HOUR_END; h++) hours.push(h);
    // grid[weekdayIdx][hourIdx] = numeric value
    const grid = HEATMAP_WEEKDAYS.map(() => hours.map(() => 0));
    let max = 0;
    let total = 0;
    let peak = null; // { dayIdx, hourIdx, value }
    const source = (allCallRecords && allCallRecords.length > 0) ? allCallRecords : mockHeatmapRecords;
    (source || []).forEach(r => {
      if (!r.call_time) return;
      const d = new Date(r.call_time);
      if (isNaN(d)) return;
      const hour = d.getHours();
      if (hour < HEATMAP_HOUR_START || hour > HEATMAP_HOUR_END) return;
      // JS Date: Sunday = 0…Saturday = 6. Shift to Mon=0…Sun=6.
      const jsDay = d.getDay();
      const dayIdx = jsDay === 0 ? 6 : jsDay - 1;
      const hourIdx = hour - HEATMAP_HOUR_START;
      const value = heatmapMode === 'duration'
        ? Math.max(0, (r.call_duration_seconds || 0)) / 60
        : 1;
      grid[dayIdx][hourIdx] += value;
      total += value;
      if (grid[dayIdx][hourIdx] > max) {
        max = grid[dayIdx][hourIdx];
        peak = { dayIdx, hourIdx, value: grid[dayIdx][hourIdx] };
      }
    });
    return { hours, grid, max, total, peak };
  }, [allCallRecords, mockHeatmapRecords, heatmapMode]);

  // Daily trend data for the stacked area chart. Two modes share the same
  // bucketing logic (per-day aggregate), just differ in which dimension they
  // stack on:
  //   - 'reasons'     → one series per call_reason key (booking, info, …)
  //   - 'performance' → three series: direkt_erledigt, weitergeleitet, offen
  const trendData = useMemo(() => {
    const source = (allCallRecords && allCallRecords.length > 0) ? allCallRecords : mockHeatmapRecords;
    if (!source || source.length === 0) {
      return { rows: [], series: [], total: 0 };
    }
    // Determine the full date range from min/max timestamps so days with zero
    // calls still appear on the X-axis (no gaps).
    const timestamps = source.map(r => r.call_time ? new Date(r.call_time).getTime() : null).filter(Boolean);
    if (timestamps.length === 0) return { rows: [], series: [], total: 0 };
    const minDate = new Date(Math.min(...timestamps));
    const maxDate = new Date(Math.max(...timestamps));
    minDate.setHours(0, 0, 0, 0);
    maxDate.setHours(0, 0, 0, 0);
    const days = [];
    for (let t = minDate.getTime(); t <= maxDate.getTime(); t += 24 * 60 * 60 * 1000) {
      days.push(new Date(t));
    }
    const fmt = (d) => `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.`;
    // Init rows
    const rowsMap = new Map();
    days.forEach(d => rowsMap.set(isoDay(d), { date: fmt(d), _iso: isoDay(d) }));
    // Series definitions per mode
    let series;
    if (trendMode === 'reasons') {
      // Stack order matches the pie's visual hierarchy (largest at the bottom).
      const reasonOrder = ['booking', 'moving', 'cancelling', 'info', 'callback', 'other'];
      series = reasonOrder.map(key => ({
        key,
        label: CALL_REASON_CONFIG[key]?.label || key,
        color: CALL_REASON_CONFIG[key]?.bg || '#9ca3af',
      }));
      source.forEach(r => {
        if (!r.call_time) return;
        const d = new Date(r.call_time);
        if (isNaN(d)) return;
        const key = isoDay(d);
        const row = rowsMap.get(key);
        if (!row) return;
        const reasons = Array.isArray(r.call_reasons) ? r.call_reasons : [];
        if (reasons.length === 0) {
          row.other = (row.other || 0) + 1;
        } else {
          reasons.forEach(rn => {
            const seriesKey = series.find(s => s.key === rn) ? rn : 'other';
            row[seriesKey] = (row[seriesKey] || 0) + 1;
          });
        }
      });
    } else {
      // performance: four series matching the Performance pie, weighted the same way.
      // KEEP IN SYNC with KPI_AUTOMATED_STATUSES / KPI_FOLLOWUP_STATUS /
      // KPI_FORWARDED_STATUS / FWD_ANSWERED in routers/call_statistics.py — the pie uses
      // the backend aggregate, this re-derives from raw rows.
      series = [
        { key: 'direkt_erledigt',           label: 'Direkt erledigt',                 color: '#1b3f65' },
        { key: 'weitergeleitet_angenommen', label: 'Weitergeleitet, angenommen',      color: '#6b829b' },
        { key: 'weitergeleitet',            label: 'Weitergeleitet, nicht bestätigt', color: '#2F6BD7' },
        { key: 'offen',                     label: 'Offen',                           color: '#fa8862' },
      ];
      source.forEach(r => {
        if (r.call_type === 'early_hangup') return;  // excluded, as in the pie
        if (!r.call_time) return;
        const d = new Date(r.call_time);
        if (isNaN(d)) return;
        const key = isoDay(d);
        const row = rowsMap.get(key);
        if (!row) return;
        // Forwards get their own bands next to "Direkt erledigt", matching the donut's
        // split (same order as the backend CASE); all three stay inside the non-open part.
        const seriesKey = r.task_status === 'weitergeleitet'
          ? 'weitergeleitet'
          : (r.was_forwarded && r.task_status === 'completed_by_deskbot') ? 'weitergeleitet_angenommen'
          : (r.task_status === 'completed_by_deskbot' || r.task_status === 'completed_with_followup') ? 'direkt_erledigt' : 'offen';
        row[seriesKey] = (row[seriesKey] || 0) + 1;
        // ...plus the follow-up task the call left behind, which is always pending.
        if (r.task_status === 'completed_with_followup') row.offen = (row.offen || 0) + 1;
      });
    }
    const rows = Array.from(rowsMap.values()).sort((a, b) => a._iso.localeCompare(b._iso));
    // Fill missing series with 0 so Recharts treats them as continuous lines.
    rows.forEach(row => series.forEach(s => { if (row[s.key] === undefined) row[s.key] = 0; }));
    return { rows, series, total: source.length };
  }, [allCallRecords, mockHeatmapRecords, trendMode]);

  // Render-time guard: wait for PracticeContext to resolve before rendering.
  if (practiceLoading) {
    return <Layout><div className="loading">Laden...</div></Layout>;
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="dashboard-page">
          <h1 className="dash-title" style={{ marginBottom: 20 }}>Dashboard</h1>
          <div style={{ textAlign: 'center', padding: '4rem 0' }}>
            <div className="spinner"></div>
            <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Lade Dashboard-Daten...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (errorMessage) {
    return (
      <Layout>
        <div className="dashboard-page">
          <h1 className="dash-title" style={{ marginBottom: 20 }}>Dashboard</h1>
          <div className="card" style={{ padding: '2rem', textAlign: 'center', background: '#fee2e2', border: '1px solid #ef4444' }}>
            <p style={{ color: '#dc2626', margin: 0 }}>{errorMessage}</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Trigger shows the dates once a custom range is applied — what you're looking
  // at, not which menu entry produced it.
  const periodLabel = timePeriod === 'range'
    ? formatDeRange(customRange.from, customRange.to)
    : (TIME_PERIOD_OPTIONS.find(o => o.value === timePeriod)?.label || timePeriod);

  // Opening the drawer without a committed range pre-fills the active preset's
  // window, so the user edits a populated range instead of two empty fields.
  const seedFrom = calculateDateRange(timePeriod === 'range' ? 'week' : timePeriod);
  const seedRange = { from: seedFrom.startDate, to: seedFrom.endDate };

  // ---- presentation-only derivations for the top section ----
  // Renders the 𐤃 glyph inside a label string (same rule the pie legend used).
  const withDalet = (value) => {
    if (typeof value === 'string' && value.includes('\u{10903}')) {
      const parts = value.split('\u{10903}');
      return <>{parts[0]}<DaletChar verticalAlign="-4px" />{parts[1]}</>;
    }
    return value;
  };
  const pctOf = (value, total) => (total > 0 ? Math.round((value / total) * 100) : 0);

  // One ranked row, shared by Anrufgründe and Übergaben an SmartAB. A row is a
  // button only when it can actually drill through: not while aggregating, and
  // not when the entry carries no filter key. Otherwise it is a plain div, which
  // has no cursor, no hover tint and no tab stop — .dash-row alone is inert, the
  // interactive styling lives entirely in the --click modifier.
  const rankedRow = (key, onClick, children) => (
    allPractices || !onClick
      ? <div key={key} className="dash-row">{children}</div>
      : <button key={key} type="button" className="dash-row dash-row--click" onClick={onClick}>{children}</button>
  );

  const perfTotal = pieChartData.reduce((sum, d) => sum + d.value, 0);
  // "Direkt erledigt" + "Weitergeleitet, angenommen": the same number the centre showed
  // before picked-up forwards got their own slice.
  const perfDone = pieChartData
    .filter((d) => d.filter === 'completed_by_deskbot' || d.filter === 'forwarded_answered')
    .reduce((sum, d) => sum + d.value, 0);
  const perfPct = pctOf(perfDone, perfTotal);

  const reasonsTotal = callReasonsData.reduce((sum, d) => sum + d.value, 0);
  const reasonsRanked = [...callReasonsData].sort((a, b) => b.value - a.value);

  const callTypesTotal = callTypesData.reduce((sum, d) => sum + d.value, 0);

  return (
    <Layout>
        <div className={`dashboard-page${allPractices ? ' is-aggregate' : ''}`}>
          {/* ── Page header: title and Zeitraum trigger on one line.
              padding-right keeps the row clear of the absolutely
              positioned practice logo (150px @ right:32px) — which App.css hides
              while aggregating, since no single practice owns the view then. ── */}
          <div className="dash-head">
            <h1 className="dash-title">Dashboard</h1>

            <span className="dash-head-sep" />

            <div className="dash-period-wrap" ref={timePeriodRef}>
              <button
                type="button"
                className={`dash-period${timePeriodOpen ? ' is-open' : ''}`}
                title={timePeriod === 'range' ? formatDeRangeFull(customRange.from, customRange.to) : undefined}
                aria-haspopup="listbox"
                aria-expanded={timePeriodOpen}
                onClick={() => setTimePeriodOpen(prev => !prev)}
              >
                {periodLabel}
                <svg className="dash-period-chev" width="10" height="10" viewBox="0 0 12 12" aria-hidden="true">
                  <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {timePeriodOpen && (
                <div
                  className="dash-period-pop"
                  role="listbox"
                  style={{
                    // Fixed widths (not 'max-content') so the popover can animate as
                    // it unfolds — you can't transition to or from an intrinsic width.
                    width: rangeOpen ? 316 : 208,
                  }}
                >
                  {TIME_PERIOD_OPTIONS.map(opt => (
                    <div
                      key={opt.value}
                      role="option"
                      aria-selected={timePeriod === opt.value}
                      className={`dash-period-opt${timePeriod === opt.value ? ' is-active' : ''}`}
                      onClick={() => { setTimePeriod(opt.value); setRangeOpen(false); setTimePeriodOpen(false); }}
                    >
                      {opt.label}
                    </div>
                  ))}

                  <DateRangeDrawer
                    open={rangeOpen}
                    committed={timePeriod === 'range' ? customRange : null}
                    seed={seedRange}
                    onToggle={() => setRangeOpen(prev => !prev)}
                    onApply={(range) => {
                      setCustomRange(range);
                      setTimePeriod('range');
                      setRangeOpen(false);
                      setTimePeriodOpen(false);
                    }}
                  />
                </div>
              )}
            </div>

            {/* Praxis-Umfang: the shared toggle, also rendered on the Tasks page.
                Only offered when there is more than one practice to sum; the
                counts come from the scope endpoint, not the practice switcher.
                Its note says so when a practice could not be loaded, instead of
                quietly showing numbers that are short one practice. */}
            <PracticeScopeToggle
              scope={scope}
              separator
              onSelect={() => setErrorMessage('')}
              loadedCount={aggregateData?.loadedCount}
              scopeCount={aggregateData?.scopeCount}
            />
          </div>

          {/* ── Kennzahlen-Band: call volume (plus its Anrufart composition) on the
              left, the six task counters on the right, one card, one row. ── */}
          <div className="card kpi-row dash-band">
            <div className="dash-band-group dash-band-calls">
              <div className="dash-stats">
                <div className="dash-stat">
                  <span className="dash-stat-value">
                    {callStats && callStats.total_calls !== undefined ? callStats.total_calls : '–'}
                  </span>
                  <span className="dash-stat-label">Anrufe</span>
                </div>
                <div className="dash-stat">
                  <span className="dash-stat-value">
                    {callStats && callStats.call_type_breakdown !== undefined ? ((callStats.call_type_breakdown?.ki || 0) + (callStats.call_type_breakdown?.forwarded || 0) + (callStats.call_type_breakdown?.forwarded_answered || 0)) : '–'}
                  </span>
                  <span className="dash-stat-label"><DaletChar verticalAlign="-3px" /> Gespräche</span>
                </div>
                <div className="dash-stat">
                  <span className="dash-stat-value">
                    {callStats && callStats.completed_call_duration_seconds !== undefined
                      ? Math.round(callStats.completed_call_duration_seconds / 60)
                      : '–'}
                  </span>
                  <span className="dash-stat-label">Gesprächs-Minuten</span>
                </div>
              </div>

              {/* Anrufart as the composition of the Anrufe total, not a fourth pie. */}
              {callTypesData.length > 0 ? (
                <div className="dash-split">
                  <div className="dash-split-bar">
                    {callTypesData.map((t) => (
                      <span
                        key={t.name}
                        className="dash-split-seg"
                        style={{ width: `${callTypesTotal > 0 ? (t.value / callTypesTotal) * 100 : 0}%`, background: t.color }}
                        title={`${t.name}: ${t.value}`}
                      />
                    ))}
                  </div>
                  <div className="dash-split-legend">
                    {callTypesData.map((t) => (
                      <span key={t.name} className="dash-split-item" title={t.tooltip}>
                        <i className="dash-dot" style={{ background: t.color }} />
                        {t.name}
                        <b>{t.value}</b>
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="dash-split-empty">Keine Anrufdaten verfügbar</div>
              )}
            </div>

            <div className="dash-band-group dash-band-tasks">
              {taskCounts ? (
                <div className="dash-tasks">
                  {TASK_TILES.map((tile) => {
                    const value = <span className={`dash-task-value${tile.alert ? ' dash-task-value--alert' : ''}`}>{taskCounts[tile.status]}</span>;
                    const label = <span className="dash-task-label">{tile.dalet ? <><DaletChar verticalAlign="-3px" /> - {tile.label}</> : tile.label}</span>;
                    // Aggregating: a plain div, so the tile carries no cursor,
                    // no hover, no focus ring and no tab stop.
                    return allPractices ? (
                      <div key={tile.status} className="dash-task dash-task--static">{value}{label}</div>
                    ) : (
                      <button key={tile.status} type="button" className="dash-task" onClick={() => handleStatusClick(tile.status)}>
                        {value}{label}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="dash-empty">Keine Aufgaben verfügbar</p>
              )}
            </div>
          </div>

          {/* ── Panels: one donut (Performance) plus two ranked lists. Anrufart
              moved into the Kennzahlen-Band above, so three equal columns fit a
              laptop width without the old 3+1 wrap. ── */}
          <div className="charts-row dash-panels">
            {/* Performance — donut with the automation rate in the middle. */}
            <div className="card dash-panel">
              <div className="dash-panel-head">
                <h3 className="dash-panel-title"><DaletChar verticalAlign="-3px" /> Performance</h3>
              </div>
              {pieChartData.length > 0 ? (
                <>
                  <div className="dash-donut">
                    <ResponsiveContainer width="100%" height={158}>
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={70}
                          paddingAngle={2}
                          stroke="none"
                          dataKey="value"
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            fontSize: '13px',
                            boxShadow: '0 6px 20px rgba(27,63,101,0.10)',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="dash-donut-center">
                      <span className="dash-donut-value">{perfPct}<small> %</small></span>
                    </div>
                  </div>
                  <div className="dash-rows">
                    {pieChartData.map((entry) => (
                      <div key={entry.name} className="dash-row" title={entry.tooltip}>
                        <i className="dash-dot" style={{ background: entry.color }} />
                        <span className="dash-row-label">{withDalet(entry.name)}</span>
                        <span className="dash-row-value">{entry.value}</span>
                        <span className="dash-row-pct">{pctOf(entry.value, perfTotal)} %</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="dash-empty">Keine Daten verfügbar</p>
              )}
            </div>

            {/* Automatisierung — super admins only: the same ring, the automation score in the middle. */}
            {isUserSuperAdmin && (
              <div className="card dash-panel">
                <div className="dash-panel-head">
                  <h3 className="dash-panel-title">
                    <DaletChar verticalAlign="-3px" /> Automatisierung
                    <span
                      className="th-info-icon th-info-icon--list"
                      tabIndex={0}
                      role="img"
                      aria-label={AUTOMATION_TOOLTIP}
                      data-info-tooltip={AUTOMATION_TOOLTIP}
                    >ⓘ</span>
                  </h3>
                </div>
                {automationPct !== null ? (
                  <div className="dash-donut">
                    <ResponsiveContainer width="100%" height={158}>
                      <PieChart>
                        <Pie
                          data={[{ value: automationPct }, { value: 100 - automationPct }]}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={70}
                          startAngle={90}
                          endAngle={-270}
                          stroke="none"
                          dataKey="value"
                          isAnimationActive={false}
                        >
                          <Cell fill="var(--color-main)" />
                          <Cell fill="var(--border-light)" />
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="dash-donut-center">
                      <span className="dash-donut-value">{automationPct}<small> %</small></span>
                    </div>
                  </div>
                ) : (
                  <p className="dash-empty">Keine Daten verfügbar</p>
                )}
              </div>
            )}

            {/* Anrufgründe — ranked list, each row still navigates to /tasks. */}
            <div className="card dash-panel">
              <div className="dash-panel-head">
                <h3 className="dash-panel-title">Anrufgründe</h3>
                {reasonsTotal > 0 && <span className="dash-panel-meta">{reasonsTotal}</span>}
              </div>
              {reasonsRanked.length > 0 ? (
                <div className="dash-rows">
                  {reasonsRanked.map((entry) => rankedRow(
                    entry.filterKey || entry.name,
                    entry.filterKey ? () => handleCallReasonClick(entry.filterKey) : null,
                    <>
                      <i className="dash-dot" style={{ background: entry.color }} />
                      <span className="dash-row-label">{entry.name}</span>
                      <span className="dash-row-value">{entry.value}</span>
                      <span className="dash-row-pct">{pctOf(entry.value, reasonsTotal)} %</span>
                    </>
                  ))}
                </div>
              ) : (
                <p className="dash-empty">Keine Anrufgründe erfasst</p>
              )}
            </div>

            {/* Übergaben an SmartAB — ranked list, each row navigates to /tasks;
                tail collapses behind one toggle. */}
            <div className="card dash-panel">
              <div className="dash-panel-head">
                <h3 className="dash-panel-title">Übergaben an SmartAB</h3>
                {escapeData.total > 0 && (
                  <span className="dash-panel-meta">
                    {escapeData.total} von {escapeData.scopeTotal}
                    {escapeData.scopeTotal > 0 && ` · ${escapeData.rate.toFixed(1).replace('.', ',')} %`}
                  </span>
                )}
              </div>
              {escapeData.total > 0 ? (
                <div className="dash-rows">
                  {(escapeShowAll ? escapeData.rows : escapeData.rows.slice(0, ESCAPE_TOP_N)).flatMap((row) => [rankedRow(
                    row.key,
                    () => handleEscapeReasonClick(row.key),
                    <>
                      <span className="dash-row-label">
                        {row.label}
                        {row.isKeyword && <span className="dash-row-tag">Schlüsselwort</span>}
                      </span>
                      <span className="dash-row-value">{row.count}</span>
                      <span className="dash-row-pct">{pctOf(row.count, escapeData.total)} %</span>
                    </>
                  ),
                  // Zusatzgrund breakdown under its row. Display only: the row's
                  // click already lands on all of them.
                  ...row.children.map((sub) => (
                    <div key={`${row.key}::${sub.label}`} className="dash-row dash-row--sub">
                      <span className="dash-row-label">{sub.label}</span>
                      <span className="dash-row-value">{sub.count}</span>
                      <span className="dash-row-pct">{pctOf(sub.count, escapeData.total)} %</span>
                    </div>
                  ))])}
                  {escapeData.rows.length > ESCAPE_TOP_N && (
                    <button
                      type="button"
                      className="dash-more"
                      onClick={() => setEscapeShowAll((v) => !v)}
                    >
                      {escapeShowAll
                        ? 'Weniger anzeigen'
                        : `Weitere Gründe (${escapeData.rows.length - ESCAPE_TOP_N})`}
                    </button>
                  )}
                </div>
              ) : (
                <p className="dash-empty">Keine Übergaben in diesem Zeitraum</p>
              )}
            </div>
          </div>

        {/* Wrapper swaps the visual order of the two charts via
            flex-direction: column-reverse — Anrufverlauf renders ABOVE the
            Anrufaufkommen heatmap, while the JSX order stays heatmap → trend. */}
        <div style={{ display: 'flex', flexDirection: 'column-reverse', gap: '1.3rem', marginTop: '1.3rem' }}>
        {/* Anrufaufkommen Heatmap — full-width row below the three pies.
            7 × HOUR_RANGE grid, color intensity = call volume, pulse on the
            hottest cell, insight badge with the peak slot. */}
        <div className="card" style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: 12 }}>
            <h3 className="h3" style={{ margin: 0 }}>Anrufaufkommen</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {heatmapData.peak && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: 'color-mix(in srgb, var(--color-main) 8%, #fff)',
                  border: '1px solid color-mix(in srgb, var(--color-main) 18%, transparent)',
                  fontSize: '0.8rem',
                  color: 'var(--color-main)',
                  fontWeight: 500,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-main)' }} />
                  Stoßzeit: <strong>{HEATMAP_WEEKDAYS[heatmapData.peak.dayIdx]}</strong>{' '}
                  <strong>{String(heatmapData.hours[heatmapData.peak.hourIdx]).padStart(2,'0')}:00</strong>
                  {' · '}{heatmapMode === 'duration'
                    ? `${Math.round(heatmapData.peak.value)} Min`
                    : `${heatmapData.peak.value} ${heatmapData.peak.value === 1 ? 'Anruf' : 'Anrufe'}`}
                </span>
              )}
              {/* Mode toggle pill (Anzahl | Dauer) — same look as TasksPage Offen/Alle */}
              <div className="heatmap-mode-toggle" role="tablist" aria-label="Heatmap-Modus">
                <button
                  type="button"
                  role="tab"
                  aria-selected={heatmapMode === 'count'}
                  className={`heatmap-mode-pill${heatmapMode === 'count' ? ' is-active' : ''}`}
                  onClick={() => setHeatmapMode('count')}
                >Anzahl</button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={heatmapMode === 'duration'}
                  className={`heatmap-mode-pill${heatmapMode === 'duration' ? ' is-active' : ''}`}
                  onClick={() => setHeatmapMode('duration')}
                >Dauer</button>
              </div>
            </div>
          </div>

          {heatmapData.total > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ display: 'inline-block', minWidth: '100%' }}>
                {/* Single grid for all hours + labels — horizontal and vertical
                    gaps come from the same `gap` property so they're guaranteed
                    identical (no marginBottom mixing). */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: `48px repeat(${HEATMAP_WEEKDAYS.length}, 1fr)`,
                  gap: 2,
                }}>
                  {heatmapData.hours.map((h, hourIdx) => (
                    <React.Fragment key={h}>
                      <div style={{
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        color: 'var(--text-secondary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        paddingRight: 8,
                      }}>{String(h).padStart(2, '0')}:00</div>
                      {HEATMAP_WEEKDAYS.map((label, dayIdx) => {
                        const value = heatmapData.grid[dayIdx][hourIdx];
                        const ratio = heatmapData.max > 0 ? value / heatmapData.max : 0;
                        const isPeak = heatmapData.peak
                          && heatmapData.peak.dayIdx === dayIdx
                          && heatmapData.peak.hourIdx === hourIdx;
                        let bg;
                        if (value === 0) {
                          bg = 'color-mix(in srgb, var(--color-main) 3%, #fff)';
                        } else {
                          const pct = Math.max(8, Math.round(ratio * 100));
                          bg = `color-mix(in srgb, var(--color-main) ${pct}%, #fff)`;
                        }
                        const valueLabel = heatmapMode === 'duration'
                          ? `${Math.round(value)} Min`
                          : `${value} ${value === 1 ? 'Anruf' : 'Anrufe'}`;
                        return (
                          <div
                            key={label}
                            className={`heatmap-cell${isPeak ? ' is-peak' : ''}`}
                            style={{
                              background: bg,
                              height: 22,
                              borderRadius: 4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              color: ratio >= 0.55 ? '#fff' : 'var(--color-main)',
                            }}
                            title={`${label} ${String(h).padStart(2,'0')}:00, ${valueLabel}`}
                          >
                            <span className="heatmap-cell-value">
                              {heatmapMode === 'duration' ? Math.round(value) : value}
                            </span>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                  {/* Weekday labels (last row of the same grid) */}
                  <div />
                  {HEATMAP_WEEKDAYS.map((label) => (
                    <div key={label} style={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      color: 'var(--text-secondary)',
                      textAlign: 'center',
                      paddingTop: 2,
                    }}>
                      {label}
                    </div>
                  ))}
                </div>
                {/* Legend */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 12, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  <span>0</span>
                  {[8, 25, 50, 75, 100].map(pct => (
                    <span key={pct} style={{
                      width: 18,
                      height: 12,
                      borderRadius: 3,
                      background: `color-mix(in srgb, var(--color-main) ${pct}%, #fff)`,
                    }} />
                  ))}
                  <span>
                    {heatmapMode === 'duration'
                      ? `${Math.round(heatmapData.max)} Min`
                      : heatmapData.max}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-secondary)' }}>
              <p>Noch keine Daten im gewählten Zeitraum</p>
            </div>
          )}
        </div>

        {/* Anrufverlauf — full-width stacked area chart. Toggle switches the
            stack dimension between Anrufgründe and Performance outcomes. */}
        <div className="card" style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: 12 }}>
            <h3 className="h3" style={{ margin: 0 }}>Anrufverlauf</h3>
            <div className="heatmap-mode-toggle" role="tablist" aria-label="Anrufverlauf-Modus">
              <button
                type="button"
                role="tab"
                aria-selected={trendMode === 'reasons'}
                className={`heatmap-mode-pill${trendMode === 'reasons' ? ' is-active' : ''}`}
                onClick={() => setTrendMode('reasons')}
              >Anrufgründe</button>
              <button
                type="button"
                role="tab"
                aria-selected={trendMode === 'performance'}
                className={`heatmap-mode-pill${trendMode === 'performance' ? ' is-active' : ''}`}
                onClick={() => setTrendMode('performance')}
              >Performance</button>
            </div>
          </div>

          {trendData.rows.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trendData.rows} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  {trendData.series.map(s => (
                    <linearGradient key={s.key} id={`trend-grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={s.color} stopOpacity={0.55} />
                      <stop offset="100%" stopColor={s.color} stopOpacity={0.12} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fontWeight: 700, fill: 'var(--text-secondary)' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  /* Auto-thin labels so they stay readable up to 90+ days. */
                  interval={Math.max(0, Math.floor(trendData.rows.length / 12) - 1)}
                  minTickGap={8}
                />
                <YAxis
                  tick={{ fontSize: 11, fontWeight: 700, fill: 'var(--text-secondary)' }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    fontSize: 13,
                  }}
                  labelStyle={{ fontWeight: 600, color: 'var(--color-main)' }}
                />
                <Legend
                  verticalAlign="bottom"
                  height={28}
                  wrapperStyle={{ fontSize: 13, paddingTop: 8 }}
                  iconType="circle"
                />
                {trendData.series.map(s => (
                  <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stackId="1"
                    stroke={s.color}
                    strokeWidth={1.5}
                    fill={`url(#trend-grad-${s.key})`}
                    isAnimationActive
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-secondary)' }}>
              <p>Noch keine Daten im gewählten Zeitraum</p>
            </div>
          )}
        </div>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
